import { useState } from 'react';

import type { UserData } from '~/types/user';

import { classNames } from '~/utils/classNames';
import { getUserInitials } from '~/utils/user';

type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type AvatarType = 'circle' | 'rounded';

export interface AvatarProps {
  user?: Pick<UserData, 'name' | 'avatar'>;
  size?: AvatarSize;
  type?: AvatarType;
  fallback?: 'icon' | 'initials';
  className?: string;
}

const sizeToClassMapping: Record<AvatarSize, { container: string; icon: string; rounded: string; initials: string }> = {
  xxs: { container: 'size-4', icon: 'size-3', rounded: 'rounded', initials: 'text-[8px]' },
  xs: { container: 'size-6', icon: 'size-4', rounded: 'rounded-md', initials: 'text-xs' },
  sm: { container: 'size-8', icon: 'size-5', rounded: 'rounded-lg', initials: 'text-sm' },
  md: { container: 'size-9', icon: 'size-6', rounded: 'rounded-lg', initials: 'text-base' },
  lg: { container: 'size-12', icon: 'size-6', rounded: 'rounded-xl', initials: 'text-lg' },
  xl: { container: 'size-14', icon: 'size-6', rounded: 'rounded-xl', initials: 'text-xl' },
  '2xl': { container: 'size-16', icon: 'size-6', rounded: 'rounded-2xl', initials: 'text-2xl' },
};

export const Avatar = ({ user, size = 'sm', type = 'circle', fallback = 'icon', className }: AvatarProps) => {
  const [failedToLoad, setFailedToLoad] = useState(false);

  const initials = getUserInitials(user);

  function onError() {
    setFailedToLoad(true);
  }

  return (
    <div
      className={classNames(
        sizeToClassMapping[size].container,
        'flex items-center justify-center shrink-0',
        'bg-bolt-ds-surfaceTwo text-bolt-ds-textSecondary border border-inset',
        'overflow-hidden',
        type === 'circle' ? 'rounded-full' : sizeToClassMapping[size].rounded,
        !!user?.avatar ? 'border-bolt-ds-borderOutline' : 'border-bolt-ds-inverseOnSurface',
        className,
      )}
    >
      {user?.avatar && !failedToLoad ? (
        <img
          className="w-full h-full object-cover"
          src={user.avatar}
          onError={onError}
          onLoad={() => setFailedToLoad(false)}
        />
      ) : fallback === 'initials' && initials ? (
        <span className={sizeToClassMapping[size].initials}>{initials}</span>
      ) : (
        <span className={classNames('i-ph:user', sizeToClassMapping[size].icon)} />
      )}
    </div>
  );
};
